<template>
  <div class="level req-visibility-banner" :class="setBannerStyles">
    <div class="level-item has-text-centered">
      <div class="req-visibility-banner-row m-top-8 m-bottom-8 is-size-5">
        <NrTooltip v-if="$can('update', 'Request')">
          <template v-slot:label>
            <p v-html="visibilityLabelTooltip"></p>
          </template>
          <span class="has-text-weight-semibold">Request visibility:</span>
        </NrTooltip>
        <span v-else class="has-text-weight-semibold">Request Visibility:</span>
        <NrIcon
          class="m-right-8 m-left-8"
          aria-hidden="true"
          :name="setBannerIcon"
        />
        <span>
          {{ setBannerText }}
        </span>
        <NrIconButton
          v-if="requestVisibility && $can('manage', 'Request')"
          @click="toggleEditModal(true)"
          class="m-left-8"
          :icon="{ name: 'pencil-alt', type: 'regular' }"
          aria-label="Edit request visibility"
          variant="minimal-neutral"
        />
      </div>
    </div>
    <NrModal
      :show="showEditModal"
      class="request-visibility-form"
      :has-secondary-button="true"
      title="Change request visibility"
      primary-button-text="Change visibility"
      secondary-button-text="Cancel"
      @on-primary-click="updateRequestVisibility"
      @on-secondary-click="() => toggleEditModal(false)"
      @on-close="() => toggleEditModal(false)"
    >
      <NrFormSelect
        label="Request visibility"
        name="visibility"
        v-model="request.visibility"
        :options="visibilities"
      />
    </NrModal>
  </div>
</template>

<script lang="ts">
import { mapActions, mapState } from 'vuex'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrFormSelect from '@nextrequestco/components/src/components/NrFormSelect/nr-form-select'
import {
  RequestVisibility,
  accountRequestVisibilities
} from '../../../store/globalModule'
import NrSentry from '../../../utils/nr_sentry'
import escape from 'lodash/escape'

export default {
  name: 'VisibilityBanner',
  components: {
    NrModal,
    NrIconButton,
    NrIcon,
    NrTooltip,
    NrFormSelect
  },
  data() {
    return {
      showEditVisibilityIcon: false,
      showEditModal: false
    }
  },
  props: {
    requestVisibility: {
      required: true
    },
    account: {
      required: true
    },
    departmentNames: {
      default: ''
    }
  },
  computed: {
    ...mapState('request_show', ['request']),
    visibilities(): RequestVisibility[] {
      return accountRequestVisibilities(this.account, false)
    },
    setBannerStyles() {
      return {
        'has-background-success has-text-white':
          this.requestVisibility === 'Published' ||
          this.requestVisibility === 'Published - department only',
        'has-background-orange-100':
          this.requestVisibility === 'Unpublished' ||
          this.requestVisibility === 'Department-Only' ||
          this.requestVisibility === 'Restricted' ||
          this.requestVisibility === 'All Staff',
        'has-background-warning': this.isEmbargoed
      }
    },

    setBannerIcon() {
      if (this.isPublished || this.isDepartmentPublished) {
        return 'eye'
      }
      if (this.isStaffVisible) {
        return 'users'
      }
      if (this.isDepartmentVisible) {
        return 'university'
      }
      if (this.isEmbargoed) {
        return 'hourglass-start'
      }
      if (this.isRestricted) {
        return 'lock'
      }
      if (this.isUnpublished) {
        return 'eye-slash'
      }
      return 'spinner'
    },

    visibilityLabelTooltip() {
      // Show embargoed text if this.account.embargoing_available
      const embargoedTooltipText = this.account.embargoing_available
        ? `Embargoed: Staff + requester until ${this.account.embargo_hours} hours after request closure, then visible to everyone` +
          '<br/><br/>'
        : ''
      // Show department names if any
      const departments =
        this.departmentNames !== '' ? escape(`(${this.departmentNames})`) : ''
      return (
        'Published: Everyone' +
        '<br/><br/>' +
        `${embargoedTooltipText}` +
        escape(`Staff: ${this.account.name} staff + requester`) +
        '<br/><br/>' +
        `Department: Admins, assigned departments staff ${departments}, and requester` +
        '<br/><br/>' +
        'Restricted: Admins, assigned staff, and requester' +
        '<br/>' +
        (this.account.restricted_visibilities_feature_flag
          ? `<br/> Published - department only: Admins, assigned departments staff ${departments}, and public`
          : '')
      )
    },

    setBannerText() {
      if (this.isPublished) {
        return 'Published'
      }
      if (this.isStaffVisible) {
        return 'Staff'
      }
      if (this.isDepartmentVisible) {
        return 'Department'
      }
      if (this.isEmbargoed) {
        return this.requestVisibility
      }
      if (this.isRestricted) {
        return 'Restricted'
      }
      if (this.isUnpublished) {
        return 'Unpublished'
      }
      if (this.isDepartmentPublished) {
        return this.requestVisibility
      }
      return 'Loading...'
    },

    isStaffVisible() {
      return this.requestVisibility === 'All Staff'
    },

    isEmbargoed() {
      return this.requestVisibility?.includes('Embargoed')
    },

    isPublished() {
      return this.requestVisibility === 'Published'
    },

    isUnpublished() {
      return this.requestVisibility === 'Unpublished'
    },

    isDepartmentVisible() {
      return this.requestVisibility === 'Department-Only'
    },

    isRestricted() {
      return this.requestVisibility === 'Restricted'
    },

    isDepartmentPublished() {
      return this.requestVisibility === 'Published - department only'
    }
  },

  methods: {
    ...mapActions('request_show', ['updateRequest', 'fetchTimeline']),
    toggleEditModal(state?: boolean) {
      if (state === undefined) this.showEditModal = !this.showEditModal
      this.showEditModal = state
    },
    updateRequestVisibility() {
      this.updateRequest({
        request_id: this.request.pretty_id,
        visibility: this.request.visibility
      })
        .then(() => {
          this.showEditModal = false
          this.$toasted.success('Successfully updated request visibility')
          this.fetchTimeline({
            pretty_id: this.request.pretty_id
          })
        })
        .catch((e) => {
          this.$toasted.error('There was an error updating the request')
          NrSentry.captureError(e)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@nextrequestco/components/src/styles/colors.scss';

.has-text-white .nr-button {
  color: $white;
}

.req-visibility-banner {
  box-shadow:
    0 0 0 1px rgba(28, 24, 23, 0.02),
    0 8px 16px 1px rgba(28, 24, 23, 0.1);
}
</style>

<style lang="scss">
@import '@nextrequestco/components/src/styles/colors.scss';
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.req-visibility-banner {
  margin-bottom: 0 !important;

  &.has-background-orange-100 {
    background-color: $color-orange-100;
  }
  .nr-modal__title {
    color: $grey-darker;
  }
  &-row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .nr-tooltip__content {
    max-width: 15rem;
  }
}

.request-visibility-form {
  .nr-modal__container {
    min-height: auto;
  }
}
</style>
